import { useState } from 'react';
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Field,
  FieldDescription,
  FieldLabel,
  Textarea,
} from '@monksflow/monks-ui';
import { RemoveElementButton } from '@/shared/ui/RemoveElementButton';
import { ContentLockCheckbox } from '@/shared/ui/ContentLockCheckbox';
import { useTranslation } from 'react-i18next';
import type { ElementLockState } from './sectionPicker.utils';

interface CopyEditDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (newContent: string, locked: boolean) => void;
  /** When provided, shows a danger "Remove" action that deletes the whole element. */
  onRemove?: () => void;
  tagName: string;
  currentContent: string;
  hasChildElements: boolean;
  /**
   * How the clicked element is currently locked (see `readLockState`). Enables the "Lock this
   * content" opt-out; omit in contexts with no agent to lock against (the templates-config edit
   * mode) and no checkbox is rendered.
   */
  lockState?: ElementLockState;
}

export function CopyEditDialog({ open, onClose, ...bodyProps }: CopyEditDialogProps) {
  const { t } = useTranslation('common');

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) onClose();
      }}
    >
      <DialogContent className="max-w-150">
        <DialogHeader>
          <DialogTitle>{t('sectionPicker.copyEditDialog.editContent')}</DialogTitle>
        </DialogHeader>
        {/* The body holds every piece of per-opening state and only exists while the dialog is
            open, so each opening seeds itself from the element that was just clicked. */}
        <CopyEditDialogBody onClose={onClose} {...bodyProps} />
      </DialogContent>
    </Dialog>
  );
}

function CopyEditDialogBody({
  onClose,
  onConfirm,
  onRemove,
  tagName,
  currentContent,
  hasChildElements,
  lockState,
}: Omit<CopyEditDialogProps, 'open'>) {
  const { t } = useTranslation('common');

  const [inputValue, setInputValue] = useState(currentContent);
  // Mirror the clicked section's current lock state: ticked when it's locked (by a user or template
  // marker, here or inherited), unticked when it's free. With no lockState (contexts that render no
  // checkbox) the value is inert, so default to unlocked.
  const [locked, setLocked] = useState(lockState?.locked ?? false);

  // A lock-only (or unlock-only) change is a valid submission — the user may want to protect copy
  // they are happy with, or free copy they no longer want protected, without retyping it.
  const contentChanged = inputValue.length > 0 && inputValue !== currentContent;
  const lockEditable = !!lockState && (!lockState.locked || lockState.canUnlock);
  const lockChanged = !!lockState && locked !== lockState.locked;
  const canSubmit = contentChanged || (lockChanged && lockEditable);

  const lockDisabledReason = lockState?.lockedByTemplate
    ? t('sectionPicker.copyEditDialog.lockedByTheTemplate')
    : t('sectionPicker.copyEditDialog.lockedAsPartOfALargerSection');

  const preview = currentContent
    ? currentContent.length > 60
      ? `${currentContent.substring(0, 60)}...`
      : currentContent
    : t('sectionPicker.copyEditDialog.empty');

  function handleConfirm() {
    if (!canSubmit) return;
    onConfirm(contentChanged ? inputValue : currentContent, locked);
  }

  return (
    <>
      <DialogBody className="flex flex-col gap-4">
        <div className="flex flex-col gap-1 rounded border border-border bg-muted p-3">
          <span className="text-body-sm text-muted-foreground">
            {t('sectionPicker.copyEditDialog.selectedElement')}
          </span>
          <span className="font-mono text-body-sm">
            {'<'}
            {tagName}
            {'>'} {preview}
          </span>
        </div>

        <Field>
          <FieldLabel htmlFor="copy-edit-content">
            {hasChildElements
              ? t('sectionPicker.copyEditDialog.contentHtml')
              : t('sectionPicker.copyEditDialog.textContent')}
          </FieldLabel>
          <Textarea
            id="copy-edit-content"
            placeholder={t('sectionPicker.copyEditDialog.enterContent')}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey && canSubmit) handleConfirm();
            }}
            rows={3}
            className="max-h-60"
            // eslint-disable-next-line jsx-a11y/no-autofocus
            autoFocus
          />
          {hasChildElements && (
            <FieldDescription>
              {t('sectionPicker.copyEditDialog.thisElementContainsFormattingHtml')}
            </FieldDescription>
          )}
        </Field>

        {lockState && (
          <ContentLockCheckbox
            id="copy-edit-lock"
            checked={locked}
            onChange={setLocked}
            disabled={!lockEditable}
            disabledReason={lockDisabledReason}
          />
        )}
      </DialogBody>
      <DialogFooter className={onRemove ? 'justify-between' : undefined}>
        {onRemove && <RemoveElementButton onRemove={onRemove} />}
        <div className="flex flex-row gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('sectionPicker.copyEditDialog.cancel')}
          </Button>
          <Button onClick={handleConfirm} disabled={!canSubmit}>
            {t('sectionPicker.copyEditDialog.apply')}
          </Button>
        </div>
      </DialogFooter>
    </>
  );
}
